import { Tabs } from 'antd';
import React, { useState } from 'react';

import { Datability, FormWrapper } from '../../../WidgetConfiguratorComponents/FormGroups';
import IDatability from '../../types/IDatability';
import { IWidgetConfiguratorProps } from '../../types/IWidgetBlock';
import { RoseConfigBlockState } from '../types';
import { GeneralSettingsConfigurator } from './GeneralSettingsConfigurator';
import { SpaceConfigurator } from '@mono/space-configurator';
import FlipDirection from 'utils/FlipDirection';

type WidgetConfiguratorProps = IWidgetConfiguratorProps<RoseConfigBlockState>;

const WidgetConfigurator: React.FC<WidgetConfiguratorProps> = (props) => {
    const { viewblockState, setViewblockState, baseDatability, componentsToDisplay = {} } = props;

    const [baseSpace, setBaseSpace] = useState(
        viewblockState.databilities[0]?.space ??
            baseDatability.space ?? {
                type: 'Point' as const,
                coordinates: [173, -40] as [number, number],
            }
    );

    const newBaseDatability = {
        ...baseDatability,
        space: baseSpace,
        data_structure: 'timeseries/point2d',
    };

    const onChangeDatability = (newDatability: IDatability) => {
        setViewblockState((prev) => ({
            ...prev,
            databilities: prev.databilities.map(() => newDatability),
        }));
    };

    const onUnitChange = ({ unit }: { datability: IDatability; unit: string }) => {
        setViewblockState((prev) => ({
            ...prev,
            settings: {
                ...prev.settings,
                unit,
                unitLabel: unit,
            },
        }));
    };

    const onUnitLabelChange = (unitLabel: string) => {
        setViewblockState((prev) => ({ ...prev, settings: { ...prev.settings, unitLabel } }));
    };

    const onInterpolationChange = (interpolationInterval: string) => {
        setViewblockState((prev) => ({
            ...prev,
            databilities: prev.databilities.map((datability) => ({
                ...datability,
                interpolationInterval,
            })),
        }));
    };

    return (
        <Tabs style={{ maxWidth: '100%' }}>
            <Tabs.TabPane tab='General settings' key='1'>
                <FormWrapper>
                    <GeneralSettingsConfigurator {...props} />
                </FormWrapper>
            </Tabs.TabPane>
            {componentsToDisplay.spaceSettings && (
                <Tabs.TabPane tab='Space settings' key='3'>
                    <SpaceConfigurator
                        enableSpace
                        enableSite
                        initialState={
                            {
                                space: viewblockState.databilities[0]?.space,
                                movSiteId: viewblockState.settings.movSiteId,
                            } ?? {}
                        }
                        onChange={(props) => {
                            if (!props.space) return;
                            setBaseSpace(props.space);
                            setViewblockState((prev) => {
                                return {
                                    ...prev,
                                    databilities: prev.databilities.map((datability) => ({
                                        ...datability,
                                        space: props.space,
                                    })),
                                    settings: { ...prev.settings, movSiteId: props.movSiteId },
                                };
                            });
                        }}
                    />
                </Tabs.TabPane>
            )}
            <Tabs.TabPane tab='Data definition' key='2'>
                <FormWrapper>
                    <Datability
                        onChangeDatability={onChangeDatability}
                        unit={viewblockState.settings.unit}
                        dataType={[`vector`]}
                        datability={{
                            ...newBaseDatability,
                            ...viewblockState.databilities[0],
                        }}
                        onUnitChange={onUnitChange}
                        unitLabel={viewblockState.settings.unitLabel}
                        onUnitLabelChange={onUnitLabelChange}
                        onInterpolationChange={onInterpolationChange}
                    />

                    <FlipDirection
                        datability={viewblockState.databilities[0]} //Can there be more than one datability?
                        disabled={false}
                        onUpdateDirection={(direction) => {
                            setViewblockState((prev) => {
                                return {
                                    ...prev,
                                    settings: {
                                        ...prev.settings,
                                        direction,
                                    },
                                };
                            });
                        }}
                        direction={viewblockState.settings?.direction ?? 'to'}
                        hoverText={
                            <>
                                <p>
                                    Standard for wind and wave is <b>From</b>.
                                </p>
                                <p>
                                    Standard for currents is <b>To</b>.
                                </p>
                                <p>
                                    Vector data default is <b>To</b>
                                </p>
                            </>
                        }
                    />
                </FormWrapper>
            </Tabs.TabPane>
        </Tabs>
    );
};

export default WidgetConfigurator;
